import type {
  PaymentTender,
  PaymentTenderInput,
  PaymentTenderMethod,
  ReceiptTender,
  ReceiptTenderInput,
  TenderCurrencyTotal,
} from '@palaccount/contracts';
import { Money } from '@palaccount/money';

/**
 * The tenders grid's arithmetic (S6 — RV-02…RV-06, PV-01), kept apart from
 * the component so it can be pinned without a DOM: the per-row base amount,
 * RV-06's per-currency subtotals and base grand total, RV-04's "Repeat"
 * (cheque no + 1, due date + 1 month), and the payload the two editors send.
 *
 * RATE CONVENTION — GEN-109a, as on every frozen rate in the product: units of
 * the tender's currency per ONE base unit; base = amount ÷ rate.
 */

export type TenderMethod = PaymentTenderMethod;

export const RECEIPT_METHODS: readonly TenderMethod[] = ['cash', 'card', 'bank_transfer', 'check', 'online'];
export const PAYMENT_METHODS: readonly TenderMethod[] = [...RECEIPT_METHODS, 'endorsed_check'];

export const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;
export const RATE_RE = /^\d{1,9}(\.\d{1,8})?$/;

/** One row of the grid — strings as typed, so a half-typed cell is never rejected mid-keystroke. */
export interface TenderRow {
  key: number;
  method: TenderMethod;
  currency: string;
  amount: string;
  rate: string;
  accountId: string;
  reference: string;
  chequeNo: string;
  bankName: string;
  branchNo: string;
  accountNo: string;
  dueDate: string;
  notes: string;
  /** PV-01: the in-hand cheque an endorsed row hands on; its facts are read-only. */
  chequeId: string | null;
  /** The register code once posted (ICQ-/OCQ-), for the link; null before. */
  chequeCode: string | null;
  /** FIN-10 §6 per tender: when this cheque bounced and was reversed; null while it stands. */
  bouncedAt: string | null;
}

let nextKey = 1;
export function nextTenderKey(): number {
  nextKey += 1;
  return nextKey;
}

/** The default row: one cash tender in the voucher's currency, due today (a cheque's default). */
export function blankTender(currency: string, today: string, defaults: Partial<TenderRow> = {}): TenderRow {
  return {
    key: nextTenderKey(),
    method: 'cash',
    currency,
    amount: '',
    rate: '1',
    accountId: '',
    reference: '',
    chequeNo: '',
    bankName: '',
    branchNo: '',
    accountNo: '',
    dueDate: today,
    notes: '',
    chequeId: null,
    chequeCode: null,
    bouncedAt: null,
    ...defaults,
  };
}

export function isCheque(row: Pick<TenderRow, 'method'>): boolean {
  return row.method === 'check' || row.method === 'endorsed_check';
}

/** amount ÷ rate to the cent, or null while either cell is not yet a number. */
export function tenderBaseAmount(row: Pick<TenderRow, 'amount' | 'rate'>, base: string): string | null {
  const amount = row.amount.trim();
  const rate = row.rate.trim();
  if (!MONEY_RE.test(amount) || !RATE_RE.test(rate) || /^0+(\.0+)?$/.test(rate)) return null;
  return Money.of(amount, base).dividedBy(rate).rounded().toFixed2();
}

/** RV-06 — a subtotal per currency, in first-appearance order; cells not yet numbers count 0. */
export function tenderTotals(rows: readonly TenderRow[]): TenderCurrencyTotal[] {
  const totals = new Map<string, Money>();
  for (const row of rows) {
    const code = row.currency.trim().toUpperCase();
    if (code === '') continue;
    const current = totals.get(code) ?? Money.zero(code);
    const amount = MONEY_RE.test(row.amount.trim()) ? Money.of(row.amount.trim(), code) : Money.zero(code);
    totals.set(code, current.plus(amount));
  }
  return [...totals].map(([currency, amount]) => ({ currency, amount: amount.toFixed2() }));
}

/** RV-06 — the grand total in the base currency. */
export function tendersBaseTotal(rows: readonly TenderRow[], base: string): string {
  return rows
    .reduce((sum, row) => {
      const converted = tenderBaseAmount(row, base);
      return converted === null ? sum : sum.plus(Money.of(converted, base));
    }, Money.zero(base))
    .toFixed2();
}

/**
 * The voucher's own total — what the allocation strip and the head's `amount`
 * read. Tenders in the voucher's currency count as typed; the rest count by
 * their base amount, multiplied by the voucher currency's rate when the
 * voucher is itself foreign (1 for a base-currency voucher). Mirrors the
 * server's `summarise`; the server's figure is the truth once saved.
 */
export function tendersVoucherTotal(
  rows: readonly TenderRow[],
  voucherCurrency: string,
  base: string,
  voucherRate: string | null,
): string {
  let total = Money.zero(voucherCurrency);
  for (const row of rows) {
    const code = row.currency.trim().toUpperCase();
    if (code === voucherCurrency) {
      if (MONEY_RE.test(row.amount.trim())) total = total.plus(Money.of(row.amount.trim(), voucherCurrency));
      continue;
    }
    const converted = tenderBaseAmount(row, base);
    if (converted === null) continue;
    const inVoucher = Money.of(converted, voucherCurrency);
    total = total.plus(
      voucherCurrency === base || voucherRate === null ? inVoucher : inVoucher.times(voucherRate).rounded(),
    );
  }
  return total.toFixed2();
}

/** The trailing digits + 1, zero-padding kept ("000123" → "000124"; "A-7" → "A-8"); blank stays blank. */
export function incrementChequeNo(chequeNo: string): string {
  const match = /^(.*?)(\d+)$/.exec(chequeNo.trim());
  if (!match) return chequeNo;
  const [, prefix, digits] = match;
  const next = (BigInt(digits!) + 1n).toString().padStart(digits!.length, '0');
  return `${prefix ?? ''}${next}`;
}

/** +1 calendar month, clamped to the month's last day (31 Jan → 28/29 Feb). */
export function addOneMonth(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const target = new Date(Date.UTC(year, month, 1)); // month is 0-based → next month
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target.toISOString().slice(0, 10);
}

/** RV-04's Repeat: the same cheque, numbered one up, due one month later. */
export function repeatCheque(row: TenderRow): TenderRow {
  return {
    ...row,
    key: nextTenderKey(),
    chequeNo: incrementChequeNo(row.chequeNo),
    dueDate: addOneMonth(row.dueDate),
    chequeId: null,
    chequeCode: null,
    bouncedAt: null,
  };
}

/** The server's rows, as grid state. */
export function tendersFromResponse(tenders: readonly (ReceiptTender | PaymentTender)[]): TenderRow[] {
  return tenders.map((tender) => ({
    key: nextTenderKey(),
    method: tender.method,
    currency: tender.currency ?? '',
    amount: tender.amount ?? '',
    rate: tender.rate ?? '1',
    accountId: tender.accountId ?? '',
    reference: tender.reference ?? '',
    chequeNo: tender.chequeNo ?? '',
    bankName: tender.bankName ?? '',
    branchNo: tender.branchNo ?? '',
    accountNo: tender.accountNo ?? '',
    dueDate: tender.dueDate ?? '',
    notes: tender.notes ?? '',
    chequeId: tender.chequeId,
    chequeCode: tender.chequeCode,
    bouncedAt: tender.bouncedAt,
  }));
}

/**
 * The payload: only rows with an amount (a blank row is a row the reader did
 * not finish, not a zero tender). Empty optional cells are omitted rather than
 * sent as ''. An endorsed row carries its cheque id and rate; the register's
 * facts travel server-side.
 */
export function tendersPayload(rows: readonly TenderRow[], kind: 'receipt'): ReceiptTenderInput[];
export function tendersPayload(rows: readonly TenderRow[], kind: 'payment'): PaymentTenderInput[];
export function tendersPayload(
  rows: readonly TenderRow[],
  kind: 'receipt' | 'payment',
): PaymentTenderInput[] | ReceiptTenderInput[] {
  const out: PaymentTenderInput[] = [];
  for (const row of rows) {
    const amount = row.amount.trim();
    if (row.method === 'endorsed_check') {
      // A receipt cannot carry one (TENDER_MESSAGES.endorsedChequeOnReceipt).
      if (!row.chequeId || kind === 'receipt') continue;
      out.push({
        method: 'endorsed_check',
        chequeId: row.chequeId,
        ...(RATE_RE.test(row.rate.trim()) ? { rate: row.rate.trim() } : {}),
        ...(row.notes.trim() ? { notes: row.notes.trim() } : {}),
      });
      continue;
    }
    if (!MONEY_RE.test(amount) || /^0+(\.0+)?$/.test(amount)) continue;
    out.push({
      method: row.method,
      currency: row.currency.trim().toUpperCase(),
      amount,
      ...(RATE_RE.test(row.rate.trim()) ? { rate: row.rate.trim() } : {}),
      ...(row.accountId ? { accountId: row.accountId } : {}),
      ...(row.reference.trim() ? { reference: row.reference.trim() } : {}),
      ...(row.method === 'check'
        ? {
            ...(row.chequeNo.trim() ? { chequeNo: row.chequeNo.trim() } : {}),
            ...(row.bankName.trim() ? { bankName: row.bankName.trim() } : {}),
            ...(row.branchNo.trim() ? { branchNo: row.branchNo.trim() } : {}),
            ...(row.accountNo.trim() ? { accountNo: row.accountNo.trim() } : {}),
            ...(row.dueDate ? { dueDate: row.dueDate } : {}),
          }
        : {}),
      ...(row.notes.trim() ? { notes: row.notes.trim() } : {}),
    });
  }
  return out;
}

/** The grid's own validation, in the order a reader fixes things. */
export type TenderProblem =
  | 'none'
  | 'amountFormat'
  | 'accountRequired'
  | 'chequeNoRequired'
  | 'dueDateRequired'
  | 'rateFormat';

export function validateTenders(rows: readonly TenderRow[]): TenderProblem {
  for (const row of rows) {
    const amount = row.amount.trim();
    if (row.method === 'endorsed_check') {
      if (!RATE_RE.test(row.rate.trim())) return 'rateFormat';
      continue;
    }
    if (amount === '') continue; // an unfinished row sends nothing
    if (!MONEY_RE.test(amount)) return 'amountFormat';
    if (!row.accountId) return 'accountRequired';
    if (row.rate.trim() !== '' && !RATE_RE.test(row.rate.trim())) return 'rateFormat';
    if (row.method === 'check') {
      if (!row.chequeNo.trim()) return 'chequeNoRequired';
      if (!row.dueDate) return 'dueDateRequired';
    }
  }
  return 'none';
}

/** The amount box's legacy reading — what a one-row voucher's amount is. */
export function singleTenderAmount(rows: readonly TenderRow[]): string {
  return rows.length === 1 ? rows[0]!.amount : '';
}
